import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Alert, Grid, Switch, Slider, FormControlLabel,
  Select, MenuItem, InputLabel, FormControl, Button, TextField, Paper,
  CircularProgress, IconButton, Avatar
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import { usePlatform } from '../../../contexts/PlatformContext';
import { BRANDING_FONT_OPTIONS, ensureBrandingFontsLoaded, formatBrandingFontLabel } from '../../../utils/brandingFonts';
import { UserContext } from '../../../contexts/UserContext';

const API_BASE = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.VITE_API_BASE_URL ||
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  'http://localhost:5000'
);

export default function GlobalSettingsPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const { themeName, onThemeChange } = useOutletContext() || {};
  const { refreshBranding } = usePlatform();
  const fileInputRef = useRef(null);

  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [sessionTimeout, setSessionTimeout] = useState(480);
  // Defaults to true, matching the server: a session ends when the person ends
  // it unless a system administrator decides otherwise.
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceStartsAt, setMaintenanceStartsAt] = useState('');
  const [maintenanceEndsAt, setMaintenanceEndsAt] = useState('');
  const [platformName, setPlatformName] = useState('');
  const [platformWebsiteUrl, setPlatformWebsiteUrl] = useState('');
  const [platformLogoUrl, setPlatformLogoUrl] = useState('');
  const [logoSize, setLogoSize] = useState(36);
  const [platformNameFont, setPlatformNameFont] = useState('');
  const [platformNameColor, setPlatformNameColor] = useState('');
  const [platformNameFontSize, setPlatformNameFontSize] = useState(16);
  const [platformNameFontWeight, setPlatformNameFontWeight] = useState('');
  const [brandingDisplay, setBrandingDisplay] = useState('both');
  const [brandingGap, setBrandingGap] = useState(8);
  const [supportEmail, setSupportEmail] = useState('');
  const [androidAppUrl, setAndroidAppUrl] = useState('');
  const [androidAppVersion, setAndroidAppVersion] = useState('');
  const [staffDefaultPassword, setStaffDefaultPassword] = useState('');
  const [payInstr, setPayInstr] = useState({});
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('system-settings');
  }, [user]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetchJson('/api/global-settings', { method: 'GET' });
        const settings = res?.settings || {};
        if (!active) return;
        setMaintenanceMode(Boolean(settings.maintenanceMode));
        setMaintenanceStartsAt(settings.maintenanceStartsAt ? String(settings.maintenanceStartsAt).slice(0, 16) : '');
        setMaintenanceEndsAt(settings.maintenanceEndsAt ? String(settings.maintenanceEndsAt).slice(0, 16) : '');
        setPlatformName(settings.platformName || '');
        setPlatformWebsiteUrl(settings.platformWebsiteUrl || '');
        setPlatformLogoUrl(settings.platformLogoUrl || '');
        if (Number.isFinite(settings.logoSize)) setLogoSize(settings.logoSize);
        setPlatformNameFont(settings.platformNameFont || '');
        setPlatformNameColor(settings.platformNameColor || '');
        if (Number.isFinite(settings.platformNameFontSize)) setPlatformNameFontSize(settings.platformNameFontSize);
        setPlatformNameFontWeight(settings.platformNameFontWeight || '');
        if (settings.brandingDisplay) setBrandingDisplay(settings.brandingDisplay);
        if (Number.isFinite(settings.brandingGap)) setBrandingGap(settings.brandingGap);
        setSupportEmail(settings.supportEmail || '');
        setAndroidAppUrl(settings.androidAppUrl || '');
        setAndroidAppVersion(settings.androidAppVersion || '');
        setStaffDefaultPassword(settings.staffDefaultPassword || '');
        setPayInstr(settings.subscriptionPaymentInstructions || {});
        if (settings.defaultLanguage) setDefaultLanguage(settings.defaultLanguage);
        if (Number.isFinite(settings.sessionTimeout)) setSessionTimeout(settings.sessionTimeout);
        setKeepSignedIn(settings.keepSignedIn !== false);
      } catch (e) {
        if (!active) return;
        setError(e?.message || t('globalSettings.loadError', 'Failed to load global settings'));
      }
    })();
    return () => { active = false; };
  }, [t]);

  useEffect(() => {
    ensureBrandingFontsLoaded([...BRANDING_FONT_OPTIONS, platformNameFont]);
  }, [platformNameFont]);

  const renderFontValue = (value) => {
    const fontValue = String(value || '').trim();
    if (!fontValue) return <em>{t('globalSettings.default', 'Default')}</em>;
    return (
      <Box component="span" sx={{ fontFamily: fontValue, fontSize: '0.95rem' }}>
        {formatBrandingFontLabel(fontValue)}
      </Box>
    );
  };

  const handleThemeChange = (newTheme) => {
    if (onThemeChange) onThemeChange(newTheme);
    setSuccess(t('globalSettings.saved', 'Settings saved successfully!'));
    setTimeout(() => setSuccess(''), 2500);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!canManage) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const data = await fetchJson('/api/global-settings/upload-logo', { method: 'POST', body: formData });

      if (data?.success && data.platformLogoUrl) {
        setPlatformLogoUrl(data.platformLogoUrl);
        setSuccess('Logo uploaded successfully!');
        setTimeout(() => setSuccess(''), 2500);
        try { window.dispatchEvent(new Event('platform:branding:updated')); } catch (_) {}
        refreshBranding();
      } else {
        setError(data?.message || 'Failed to upload logo');
      }
    } catch (err) {
      setError(err?.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = () => {
    if (!canManage) return;
    setPlatformLogoUrl('');
  };

  const handleSave = async () => {
    if (!canManage) return;
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = {
        maintenanceMode,
        maintenanceStartsAt: maintenanceStartsAt || null,
        maintenanceEndsAt: maintenanceEndsAt || null,
        platformName: platformName || null,
        platformWebsiteUrl: platformWebsiteUrl || null,
        platformLogoUrl: platformLogoUrl || null,
        logoSize,
        platformNameFont: platformNameFont || null,
        platformNameColor: platformNameColor || null,
        platformNameFontSize,
        platformNameFontWeight: platformNameFontWeight || null,
        brandingDisplay,
        brandingGap,
        supportEmail: supportEmail || null,
        androidAppUrl: androidAppUrl.trim(),
        androidAppVersion: androidAppVersion.trim(),
        staffDefaultPassword: staffDefaultPassword.trim(),
        subscriptionPaymentInstructions: payInstr || null,
        defaultLanguage,
        sessionTimeout,
        keepSignedIn,
        defaultDateFormat: 'DD/MM/YYYY',
        defaultPasswordLength: 8
      };
      await fetchJson('/api/global-settings', { method: 'PUT', body: payload });
      setSuccess(t('globalSettings.saved', 'Settings saved successfully!'));
      setTimeout(() => setSuccess(''), 3000);
      try { window.dispatchEvent(new Event('platform:branding:updated')); } catch (_) {}
      refreshBranding();
    } catch (e) {
      console.error('GlobalSettings save error:', e);
      setError(e?.message || t('globalSettings.saveError', 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const resolvedLogoSrc = platformLogoUrl
    ? (platformLogoUrl.startsWith('http') ? platformLogoUrl : `${String(API_BASE || '').replace(/\/$/, '')}${platformLogoUrl}`)
    : null;
  const previewPlatformName = String(platformName || '').trim() || 'Encatar';
  const previewFontFamily = String(platformNameFont || '').trim() || 'inherit';
  const previewFontWeight = String(platformNameFontWeight || '').trim() || '700';
  const previewFontSize = Number(platformNameFontSize) || 16;

  return (
    <Box maxWidth={1100} mx="auto" my={2}>
      <Typography variant="h5" fontWeight={700} mb={3}>{t('globalSettings.title', 'Global Settings')}</Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Platform Identity */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" mb={2}>{t('globalSettings.platformIdentity', 'Platform Identity')}</Typography>
            <Grid container spacing={2} alignItems="center">
              {/* Logo */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={1}>{t('globalSettings.platformLogo', 'Platform Logo')}</Typography>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  {resolvedLogoSrc ? (
                    <Box
                      component="img"
                      src={resolvedLogoSrc}
                      alt="Platform Logo"
                      sx={{ width: logoSize, height: logoSize, objectFit: 'contain', maxWidth: 280, borderRadius: 1, border: '1px solid #e0e0e0' }}
                    />
                  ) : (
                    <Avatar variant="rounded" sx={{ width: 64, height: 64, bgcolor: 'grey.200', color: 'grey.500' }}>
                      Logo
                    </Avatar>
                  )}
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || !canManage}
                      >
                        {t('globalSettings.uploadLogo', 'Upload Logo')}
                      </Button>
                      {platformLogoUrl && (
                        <IconButton size="small" color="error" onClick={handleRemoveLogo} disabled={!canManage}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleLogoUpload}
                    />
                    <TextField
                      size="small"
                      label={t('globalSettings.logoUrl', 'Or paste logo URL')}
                      value={platformLogoUrl}
                      onChange={e => setPlatformLogoUrl(e.target.value)}
                      placeholder="https://example.com/logo.png"
                      sx={{ minWidth: 320 }}
                      disabled={!canManage}
                    />
                  </Box>
                </Box>
                <Box sx={{ mt: 2, maxWidth: 320 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('globalSettings.logoSize', 'Logo Display Size')}: {logoSize}px
                  </Typography>
                  <Slider
                    min={20}
                    max={160}
                    step={2}
                    value={logoSize}
                    onChange={(_, v) => setLogoSize(v)}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}px`}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('globalSettings.platformName', 'Platform Name')} value={platformName} onChange={e => setPlatformName(e.target.value)} placeholder="e.g. Encatar" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('globalSettings.platformWebsiteUrl', 'Platform Website URL')} value={platformWebsiteUrl} onChange={e => setPlatformWebsiteUrl(e.target.value)} placeholder="https://encatar.com" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('globalSettings.supportEmail', 'Support Email')} value={supportEmail} onChange={e => setSupportEmail(e.target.value)} placeholder="support@example.com" type="email" />
              </Grid>

              {/* WHERE THE ANDROID APP IS.
                  Settings rather than a compiled-in constant, so publishing a
                  new build, moving the file or listing on Play is a field to
                  edit instead of a redeploy. Left empty, every download control
                  in the product simply is not there — which is the right answer
                  before there is anything to download. */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                  {t('globalSettings.androidAppHeading', 'Android app')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('globalSettings.androidAppHint',
                    'A Google Play listing, or a direct link to the .apk file. Store the file under the name encatar.apk and it is served straight from your host; under any other name it is passed through this server so it still downloads as encatar.apk. Do not use an Expo build link — those are deleted 14 days after the build. Leave empty to hide the download button everywhere.')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label={t('globalSettings.androidAppUrl', 'Android app link')}
                  value={androidAppUrl}
                  onChange={e => setAndroidAppUrl(e.target.value)}
                  placeholder="https://pub-xxxx.r2.dev/encatar.apk"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('globalSettings.androidAppVersion', 'App version (optional)')}
                  value={androidAppVersion}
                  onChange={e => setAndroidAppVersion(e.target.value)}
                  placeholder="1.0.0"
                />
              </Grid>

              {/* THE PASSWORD NEW STAFF ACCOUNTS OPEN WITH.
                  Here rather than compiled in, so a deployment can pick a value
                  only its own schools know instead of the one published in the
                  source. It does not apply to school administrators, whose
                  accounts stay on a generated password, and it is never used
                  for a reset. */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                  {t('globalSettings.staffPasswordHeading', 'New staff password')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('globalSettings.staffPasswordHint',
                    'Every new staff account is created with this, so the office can read it out to someone who cannot open their email. They change it from their profile. It does not apply to school administrators, and it is never used to reset a password.')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('globalSettings.staffDefaultPassword', 'Opening password for new staff')}
                  value={staffDefaultPassword}
                  onChange={e => setStaffDefaultPassword(e.target.value)}
                  placeholder="encatarPW"
                  helperText={t('globalSettings.staffPasswordRule',
                    '6 to 64 characters. Leave empty to use the built-in default.')}
                />
              </Grid>

              {/* Branding Display Option */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('globalSettings.brandingDisplay', 'Branding Display')}</InputLabel>
                  <Select value={brandingDisplay} onChange={e => setBrandingDisplay(e.target.value)} label={t('globalSettings.brandingDisplay', 'Branding Display')}>
                    <MenuItem value="both">{t('globalSettings.brandingBoth', 'Logo & Name')}</MenuItem>
                    <MenuItem value="logoOnly">{t('globalSettings.brandingLogoOnly', 'Logo Only')}</MenuItem>
                    <MenuItem value="nameOnly">{t('globalSettings.brandingNameOnly', 'Name Only')}</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                  {t('globalSettings.brandingDisplayHint', 'Controls what is shown on the sidebar and login page')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('globalSettings.brandingGap', 'Logo–Name Gap')}: {brandingGap}px
                </Typography>
                <Slider
                  min={0}
                  max={32}
                  step={1}
                  value={brandingGap}
                  onChange={(_, v) => setBrandingGap(v)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}px`}
                  sx={{ maxWidth: 280 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('globalSettings.brandingGapHint', 'Space between logo and app name in the sidebar')}
                </Typography>
              </Grid>

              {/* App Name Styling for Side Menubar */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" mt={1} mb={1}>{t('globalSettings.nameStyle', 'App Name Style (Sidebar)')}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('globalSettings.nameFont', 'Font Family')}</InputLabel>
                  <Select value={platformNameFont} onChange={e => setPlatformNameFont(e.target.value)} label={t('globalSettings.nameFont', 'Font Family')} renderValue={renderFontValue}>
                    <MenuItem value=""><em>{t('globalSettings.default', 'Default')}</em></MenuItem>
                    {BRANDING_FONT_OPTIONS.map((font) => (
                      <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                        <Typography component="span" sx={{ fontFamily: font, fontSize: '0.95rem' }}>
                          {font}
                        </Typography>
                      </MenuItem>
                    ))}
                    <MenuItem value="serif" sx={{ fontFamily: 'serif' }}><Typography component="span" sx={{ fontFamily: 'serif', fontSize: '0.95rem' }}>Serif</Typography></MenuItem>
                    <MenuItem value="sans-serif" sx={{ fontFamily: 'sans-serif' }}><Typography component="span" sx={{ fontFamily: 'sans-serif', fontSize: '0.95rem' }}>Sans Serif</Typography></MenuItem>
                    <MenuItem value="monospace" sx={{ fontFamily: 'monospace' }}><Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>Monospace</Typography></MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('globalSettings.nameColor', 'Text Color')}
                  value={platformNameColor}
                  onChange={e => setPlatformNameColor(e.target.value)}
                  placeholder="#ffffff"
                  InputProps={{
                    startAdornment: (
                      <Box
                        component="input"
                        type="color"
                        value={platformNameColor || '#ffffff'}
                        onChange={e => setPlatformNameColor(e.target.value)}
                        sx={{ width: 28, height: 28, border: 'none', background: 'none', cursor: 'pointer', p: 0, mr: 1 }}
                      />
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('globalSettings.nameFontSize', 'Font Size')}: {platformNameFontSize}px
                </Typography>
                <Slider
                  min={10}
                  max={36}
                  step={1}
                  value={platformNameFontSize}
                  onChange={(_, v) => setPlatformNameFontSize(v)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}px`}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('globalSettings.nameFontWeight', 'Font Weight')}</InputLabel>
                  <Select value={platformNameFontWeight} onChange={e => setPlatformNameFontWeight(e.target.value)} label={t('globalSettings.nameFontWeight', 'Font Weight')}>
                    <MenuItem value=""><em>{t('globalSettings.default', 'Default')}</em></MenuItem>
                    <MenuItem value="300">300 (Light)</MenuItem>
                    <MenuItem value="400">400 (Regular)</MenuItem>
                    <MenuItem value="500">500 (Medium)</MenuItem>
                    <MenuItem value="600">600 (SemiBold)</MenuItem>
                    <MenuItem value="700">700 (Bold)</MenuItem>
                    <MenuItem value="800">800 (ExtraBold)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 }, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('globalSettings.preview', 'Live Preview')}
                  </Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: `${brandingGap}px`, minHeight: logoSize + 8, maxWidth: '100%' }}>
                    {resolvedLogoSrc && brandingDisplay !== 'nameOnly' && (
                      <Box component="img" src={resolvedLogoSrc} alt="Platform Logo" sx={{ width: logoSize, height: logoSize, objectFit: 'contain', borderRadius: 1, flexShrink: 0 }} />
                    )}
                    {brandingDisplay !== 'logoOnly' && (
                      <Typography noWrap sx={{ fontFamily: previewFontFamily, color: platformNameColor || 'text.primary', fontSize: `${previewFontSize}px`, fontWeight: previewFontWeight, letterSpacing: 0.2, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {previewPlatformName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Default Theme & Language for new schools */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" mb={2}>{t('globalSettings.defaults', 'Defaults for New Schools')}</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('globalSettings.defaultsDesc', 'These are the default theme and language new schools will see. They can change these in their own settings.')}
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('globalSettings.defaultLanguage', 'Default Language')}</InputLabel>
              <Select value={defaultLanguage} onChange={e => setDefaultLanguage(e.target.value)} label={t('globalSettings.defaultLanguage', 'Default Language')}>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="fr">Français</MenuItem>
              </Select>
            </FormControl>
          </Paper>
        </Grid>

        {/* Session & Security */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" mb={2}>{t('globalSettings.security', 'Security')}</Typography>

            {/* STAY SIGNED IN UNTIL SIGNING OUT — on by default.
                The timeout below is an IDLE timer: it signs someone out for not
                touching the screen, which in a classroom mostly means losing a
                half-entered register. A teacher marking a book for ten minutes
                came back to a login form. Turning this off restores the idle
                timer for every portal, which is why the slider stays but is
                disabled while this is on rather than being hidden — an
                administrator should be able to see what they are turning on. */}
            <FormControlLabel
              control={<Switch checked={keepSignedIn} onChange={e => setKeepSignedIn(e.target.checked)} />}
              label={t('globalSettings.keepSignedIn', 'Keep users signed in until they sign out')}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {keepSignedIn
                ? t('globalSettings.keepSignedInOn', 'Sessions end only when someone signs out. Nobody is signed out for being idle.')
                : t('globalSettings.keepSignedInOff', 'Sessions end after the idle period below, on every portal.')}
            </Typography>

            <Typography variant="subtitle2" mb={1} sx={{ opacity: keepSignedIn ? 0.5 : 1 }}>{t('globalSettings.sessionTimeout', 'Session Timeout (minutes)')}: {sessionTimeout >= 60 ? `${Math.floor(sessionTimeout / 60)}h ${sessionTimeout % 60 > 0 ? (sessionTimeout % 60) + 'm' : ''}` : `${sessionTimeout}m`}</Typography>
            <Slider min={480} max={1440} step={60} value={sessionTimeout} disabled={keepSignedIn} onChange={(_, v) => setSessionTimeout(v)} valueLabelDisplay="auto" valueLabelFormat={(v) => `${Math.floor(v / 60)}h`} />
            <Typography variant="body2" color="text.secondary" mt={1}>
              {t('globalSettings.passwordNote', 'Default password length is 8 characters for all generated passwords.')}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              {t('globalSettings.dateNote', 'System date format: DD/MM/YYYY (applied globally).')}
            </Typography>
          </Paper>
        </Grid>

        {/* Maintenance Mode */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" mb={1}>{t('globalSettings.maintenance', 'Maintenance Mode')}</Typography>
            <FormControlLabel control={<Switch checked={maintenanceMode} onChange={e => setMaintenanceMode(e.target.checked)} />} label={t('globalSettings.maintenanceToggle', 'Enable Maintenance Mode')} />
            <Typography variant="body2" color="text.secondary" mt={1}>
              {t('globalSettings.maintenanceDesc', 'When enabled, the system will be in maintenance mode for all users.')}
            </Typography>
            {maintenanceMode && (
              <Box mt={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <CalendarMonthIcon color="action" />
                <TextField
                  type="datetime-local"
                  label={t('globalSettings.maintenanceStart', 'Start')}
                  value={maintenanceStartsAt}
                  onChange={e => setMaintenanceStartsAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 250 }}
                />
                <TextField
                  type="datetime-local"
                  label={t('globalSettings.maintenanceEnd', 'End')}
                  value={maintenanceEndsAt}
                  onChange={e => setMaintenanceEndsAt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 250 }}
                />
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Subscription Payment Instructions (shown to schools on the renewal page) */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" mb={1}>{t('globalSettings.payInstr.title', 'Subscription Payment Instructions')}</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('globalSettings.payInstr.desc', 'Bank and mobile money details schools should pay into for subscription renewal. These are shown on the school renewal page when paying by bank transfer or mobile money.')}
            </Typography>
            <Grid container spacing={2}>
              {[
                ['bankName', t('globalSettings.payInstr.bankName', 'Bank Name')],
                ['accountName', t('globalSettings.payInstr.accountName', 'Account Name')],
                ['accountNumber', t('globalSettings.payInstr.accountNumber', 'Account Number')],
                ['bankBranch', t('globalSettings.payInstr.bankBranch', 'Branch')],
                ['swift', t('globalSettings.payInstr.swift', 'SWIFT / BIC')],
                ['mtnNumber', t('globalSettings.payInstr.mtnNumber', 'MTN MoMo Number')],
                ['mtnName', t('globalSettings.payInstr.mtnName', 'MTN MoMo Account Name')],
                ['orangeNumber', t('globalSettings.payInstr.orangeNumber', 'Orange Money Number')],
                ['orangeName', t('globalSettings.payInstr.orangeName', 'Orange Money Account Name')],
              ].map(([key, label]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    fullWidth
                    size="small"
                    label={label}
                    value={payInstr?.[key] || ''}
                    onChange={(e) => setPayInstr((prev) => ({ ...prev, [key]: e.target.value }))}
                    disabled={!canManage}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={t('globalSettings.payInstr.notes', 'Additional Instructions')}
                  value={payInstr?.instructions || ''}
                  onChange={(e) => setPayInstr((prev) => ({ ...prev, instructions: e.target.value }))}
                  placeholder={t('globalSettings.payInstr.notesPlaceholder', 'e.g. Use your school name as the payment reference.')}
                  disabled={!canManage}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSave}
          disabled={saving || !canManage}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ minWidth: 180 }}
        >
          {saving ? t('common.saving', 'Saving...') : t('globalSettings.save', 'Save Changes')}
        </Button>
      </Box>
    </Box>
  );
}
